import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useTranslation } from '@/contexts/LanguageContext';
import {
  Rocket, TrendingUp, Star, Home, X, Loader2, Check, CreditCard
} from 'lucide-react';

export default function BoostModal({ listing, onClose }) {
  const { t } = useTranslation('boost');
  const [packages, setPackages] = useState({});
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeBoosts, setActiveBoosts] = useState({});

  const BOOST_TYPES = {
    search_top: { icon: TrendingUp, label: t('searchTop'), desc: t('searchTopDesc'), color: 'bg-blue-50 border-blue-200 text-blue-700' },
    homepage: { icon: Home, label: t('homepage'), desc: t('homepageDesc'), color: 'bg-purple-50 border-purple-200 text-purple-700' },
    badge: { icon: Star, label: t('badge'), desc: t('badgeDesc'), color: 'bg-amber-50 border-amber-200 text-amber-700' },
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [pkgResp, boostResp] = await Promise.all([
          api.get('/boost/packages'),
          api.get(`/boost/listing/${listing.id}`),
        ]);
        setPackages(pkgResp.data);
        setActiveBoosts(boostResp.data.active || {});
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, [listing.id]);

  const togglePackage = (pkgId) => {
    const pkg = packages[pkgId];
    if (!pkg) return;
    setSelected(prev => {
      const existing = prev.find(p => packages[p]?.type === pkg.type);
      if (existing === pkgId) return prev.filter(p => p !== pkgId);
      const filtered = prev.filter(p => packages[p]?.type !== pkg.type);
      return [...filtered, pkgId];
    });
  };

  const total = selected.reduce((sum, id) => sum + (packages[id]?.price || 0), 0);

  const handleCheckout = async () => {
    if (!selected.length) return;
    setProcessing(true);
    try {
      const { data } = await api.post('/boost/checkout', {
        listing_id: listing.id,
        package_ids: selected,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('paymentError'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8" onClick={e => e.stopPropagation()}>
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400 mx-auto" />
        </div>
      </div>
    );
  }

  // Group packages by type
  const grouped = {};
  Object.entries(packages).forEach(([id, pkg]) => {
    if (!grouped[pkg.type]) grouped[pkg.type] = [];
    grouped[pkg.type].push({ id, ...pkg });
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="boost-modal">
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[#F25C05]/10 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-[#F25C05]" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-neutral-900">{t('title')}</h2>
              <p className="text-xs text-neutral-500 truncate max-w-[280px]">{listing.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-neutral-100" data-testid="close-boost-modal">
            <X className="w-5 h-5 text-neutral-400" />
          </button>
        </div>

        {/* Boost Options */}
        <div className="p-5 space-y-6">
          {Object.entries(BOOST_TYPES).map(([type, meta]) => {
            const Icon = meta.icon;
            const typePackages = grouped[type] || [];
            const isActive = activeBoosts[type];

            return (
              <div key={type} className="space-y-2.5" data-testid={`boost-type-${type}`}>
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-neutral-700" />
                  <h3 className="font-semibold text-sm text-neutral-900">{meta.label}</h3>
                  {isActive && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{t('active')}</Badge>}
                </div>
                <p className="text-xs text-neutral-500">{meta.desc}</p>
                <div className="grid grid-cols-4 gap-2">
                  {typePackages.sort((a, b) => a.days - b.days).map((pkg) => {
                    const isSelected = selected.includes(pkg.id);
                    return (
                      <button
                        key={pkg.id}
                        onClick={() => togglePackage(pkg.id)}
                        className={`relative rounded-xl border-2 p-3 text-center transition-all ${
                          isSelected
                            ? 'border-[#F25C05] bg-[#F25C05]/5 ring-2 ring-[#F25C05]/20'
                            : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                        data-testid={`boost-package-${pkg.id}`}
                      >
                        {isSelected && (
                          <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#F25C05] flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                        <p className="font-bold text-lg text-neutral-900">{pkg.days}{t('days')}</p>
                        <p className="font-bold text-sm text-[#F25C05]">{pkg.price.toFixed(0)}€</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer with total & pay button */}
        <div className="border-t p-5">
          {selected.length > 0 && (
            <div className="mb-3 space-y-1">
              {selected.map(id => {
                const pkg = packages[id];
                return (
                  <div key={id} className="flex justify-between text-sm">
                    <span className="text-neutral-600">{pkg.name} ({pkg.days}{t('days')})</span>
                    <span className="font-medium">{pkg.price.toFixed(2)}€</span>
                  </div>
                );
              })}
              <div className="flex justify-between text-base font-bold pt-1 border-t">
                <span>{t('total')}</span>
                <span className="text-[#F25C05]">{total.toFixed(2)}€</span>
              </div>
            </div>
          )}
          <Button
            onClick={handleCheckout}
            disabled={!selected.length || processing}
            className="w-full bg-[#F25C05] hover:bg-[#d94f04] text-white h-11"
            data-testid="boost-pay-btn"
          >
            {processing ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <CreditCard className="w-4 h-4 mr-2" />
            )}
            {selected.length ? `${t('pay')} ${total.toFixed(2)}€` : t('selectOption')}
          </Button>
          <div className="flex items-center justify-center gap-3 mt-3 text-[10px] text-neutral-400">
            <span>Visa</span>
            <span>Mastercard</span>
            <span>Bancontact</span>
            <span>{t('securePayment')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
